import * as React from "react";
import { Suspense, lazy } from "react";
import MInstallAppBanner from "@/components/ui/organisms/MInstallAppBanner";
import MInstallGateScreen from "@/components/ui/organisms/MInstallGateScreen";
import { Routes, Route, Navigate, useNavigate, Outlet } from "react-router-dom";
import { AuthGuard } from "./components/guards/AuthGuard";
import { AdminAuthGuard } from "./components/guards/AdminAuthGuard";
import BaseNavigation from "./pages/profile/BaseNavigation";

import BasicInformation from "./pages/profile/basic-information/BasicInformation";
import SignUpOptions from "./pages/auth/register/SignupOption";
import SignupForm from "./pages/auth/register/SignupForm";
import SignupConfirm from "./pages/auth/register/SignupConfirm";
import RegistrationSuccessSplash from "./pages/auth/register/RegistrationSuccessSplash";

import HomeLayout from "@/components/Layout/HomeLayout";
import Layout from "@/components/Layout/Layout";
import AdminLayout from "@/components/Layout/AdminLayout";
import FamilyListing from "./pages/profile/family/index";
import FamilyLanding from "./pages/profile/family/FamilyLanding";
import ManageAccess from "./pages/profile/family/manage-access/ManageAccess";
import ManageMyAccess from "./pages/profile/family/manage-access/ManageMyAccess";
import NoFooterLayout from "./components/Layout/NoFooterLayout";
import LandingPageLayout from "./components/Layout/LandingPageLayout";
import LandingPageLayoutWithBack from "./components/Layout/LandingPageLayoutWithBack";
import SuccessPageLayout from "./components/Layout/SuccessPageLayout";
import RegisterMember from "./pages/auth/register/register-member";
import Dashboard from "./pages/Dashboard/index";
import DashboardDetail from "./pages/Dashboard/DashboardDetail";
import { Toaster } from "@/components/ui/organisms/toaster";
import { AvatarProvider } from "./context/AvatarContext";
import "./i18n/config";
import { PublicRoutePath } from "./config/routes";
import RootRedirect from "./RootRedirect";
import AuthCallback from "@/pages/auth/AuthCallback";
import ManageSubscription from "./pages/Subscription/ManageSubscription";
import NewSubscription from "./pages/Subscription/NewSubscription";
import SubscriptionSuccess from "./pages/Subscription/SubscriptionSuccess";
import PurchaseHistory from "./pages/Subscription/PurchaseHistory";

import UpdateProfile from "./pages/profile/family/UpdateProfile";
import MemberDetail from "./pages/profile/family/MemberDetail";
import MemberDetailWrapper from "./pages/profile/family/MemberDetailWrapper";
import DocumentsOverview from "./pages/profile/family/DocumentsOverview";
import HealthMemoriesOverview from "./pages/profile/family/HealthMemoriesOverview";
import RemindersOverview from "./pages/profile/family/RemindersOverview";
import EmergencyCardsOverview from "./pages/profile/family/EmergencyCardsOverview";
import HealthProfilesOverview from "./pages/profile/family/HealthProfilesOverview";
import HealthSummaryOverview from "./pages/profile/family/HealthSummaryOverview";
import PillboxOverview from "./pages/profile/family/PillboxOverview";
import VitalsOverview from "./pages/profile/family/VitalsOverview";
import LabTrendsOverview from "./pages/profile/family/LabTrendsOverview";
import DocumentListing from "./pages/documents/DocumentListing";
import DocumentViewer from "./pages/documents/DocumentViewer";
import EmergencyCardViewer from "./pages/documents/EmergencyCardViewer";
import HealthSummaryLanding from "./pages/health-summary/landing";
import HealthSummaryViewer from "./pages/documents/HealthSummaryViewer";
const HealthSummaryDetails = lazy(() => import("./pages/health-summary"));
import DocumentSelection from "./pages/documents/DocumentSelection";
import UncategorizedDocuments from "./pages/documents/UncategorizedDocuments";
import NotesListing from "./pages/documents/notes/NotesListing";
import FamilyNotes from "./pages/profile/family/notes/FamilyNotes";
import FamilyNoteDetail from "./pages/profile/family/notes/FamilyNoteDetail";
import Notifications from "./pages/profile/Notifications";
import OSSLicenses from "./pages/profile/OSSLicenses";
import PrivacyAndSecurity from "./pages/profile/privacy-and-security/PrivacyAndSecurity";
import ConsentWithdrawal from "./pages/profile/privacy-and-security/ConsentWithdrawal";
import Support from "./pages/profile/Support";
const ShareDocument = React.lazy(
  () => import("./pages/documents/ShareDocument")
);
import Reminders from "./pages/reminders";
import Insights from "./pages/insights/Insights";
import LabInsights from "./pages/lab-insights/LabInsights";
import LabInsightsAllGroups from "./pages/lab-insights/LabInsightsAllGroups";
import LabInsightsGroupDetails from "./pages/lab-insights/LabInsightsGroupDetails";
import LabInsightsParameterDetails from "./pages/lab-insights/LabInsightsParameterDetails";
import Updates from "./pages/Dashboard/components/Updates";
import GrantAccessPage from "./pages/members/GrantAccess";
import ButtonTest from "./pages/UiTest/ButtonTest";
import MFilterTest from "./pages/UiTest/MFilterTest";
import ComponentDemo from "./pages/UiTest/ComponentDemo";
import InputTest from "./pages/UiTest/InputTest";
import MInfoTest from "./pages/UiTest/MInfoTest";
import AvatarTest from "./pages/UiTest/AvatarTest";
import ChipDemo from "./pages/UiTest/ChipDemo";
import ToastTest from "./pages/UiTest/ToastTest";
import TestTextArea from "./pages/UiTest/TestTextArea";
import CheckboxTest from "./pages/UiTest/CheckboxTest";
import RadioTest from "./pages/UiTest/RadioTest";
import ProgressBarTest from "./pages/UiTest/ProgressBarTest";
import SelectRelationship from "./pages/profile/family/add-member/SelectRelationship";
import AddMemberName from "@/pages/profile/family/add-member/AddMemberName";
import AddPetDetails from "@/pages/profile/family/add-member/AddPetDetails";
import AddMemberDetails from "@/pages/profile/family/add-member/AddMemberDetails";
import AddPetProfile from "@/pages/profile/family/add-member/AddPetProfile";
import DeleteProfile from "@/pages/profile/family/delete-member/DeleteProfile";
import DeleteConfirm from "@/pages/profile/family/delete-member/DeleteConfirm";
import AddMemberContact from "@/pages/profile/family/add-member/AddMemberContact";
import AddMemberConsent from "@/pages/profile/family/add-member/AddMemberConsent";
import AddMemberSuccess from "./pages/profile/family/add-member/AddMemberSuccess";
import AddMemberInvite from "./pages/profile/family/add-member/AddMemberInvite";
import InviteMember from "./pages/profile/family/invite-member/InviteMember";
import InviteSuccess from "./pages/profile/family/invite-member/InviteSuccess";
import InviteContactMethod from "./pages/profile/family/invite-member/InviteContactMethod";
import ProgressTest from "./pages/UiTest/ProgressTest";
import ToggleTest from "./pages/UiTest/ToggleTest";
import DialogTest from "./pages/UiTest/DialogTest";
import ModalDialogTest from "./pages/UiTest/ModalDialogTest";
import ListItemDemo from "./pages/UiTest/ListItemDemo";
import OptionsMenuTest from "./pages/UiTest/OptionsMenuTest";
import DrawerModulesTest from "@/pages/UiTest/DrawerModulesTest";
import MFrameTest from "./pages/UiTest/MFrameTest";
import MButtonCardTest from "./pages/UiTest/MButtonCardTest";
import MInfiniteScrollTest from "./pages/UiTest/MInfiniteScrollTest";
import Insurance from "./pages/profile/basic-information/insurance/Insurance";
import InsuranceForm from "./pages/profile/basic-information/insurance/InsuranceForm";
import InsuranceType from "./pages/profile/basic-information/insurance/InsuranceType";
import LinkFamilyPolicy from "./pages/profile/basic-information/insurance/LinkFamilyPolicy";
import AddInsurance from "./pages/profile/basic-information/insurance/AddInsurance";
import NavigationBarDemo from "./pages/UiTest/NavigationBarDemo";
import ProfilePickerDemo from "./pages/UiTest/ProfilePickerDemo";
import IntegratedMobileDemo from "@/pages/UiTest/IntegratedMobileDemo";
import SpouseAccess from "./pages/profile/family/add-member/SpouseAccess";
import UploadReviewPage from "./pages/documents/UploadReviewPage";
import Welcome from "./pages/Welcome";
import SignupLanding from "./pages/auth/SignupLanding";
import DesktopInstallPage from "./pages/DesktopInstallPage/DesktopInstallPage";
import WhatCanMaatraDo from "./pages/explore-maatra/WhatCanMaatraDo";
import HowMaatraWorks from "./pages/how-maatra-works/HowMaatraWorks";
import OngoingCare from "./pages/explore-maatra/OngoingCare";
import RemoteFamily from "./pages/explore-maatra/RemoteFamily";
import ScatteredRecords from "./pages/explore-maatra/ScatteredRecords";
import Hospitalisation from "./pages/explore-maatra/Hospitalisation";
const AdminDashboardLazy = lazy(() => import("./pages/Admin/Dashboard"));
const AdminIndexLazy = lazy(() => import("./pages/Admin"));
const AdminRolesLazy = lazy(() => import("./pages/Admin/Roles"));
// Import CSS files directly
import "./styles/no-keyboard.css"; // Prevent keyboard from showing on mobile devices

// Lazy load the AdminPrompts component
const AdminPromptsLazy = React.lazy(() => import("./pages/Admin/Prompt"));
const AdminMedicineLazy = lazy(() => import("./pages/Admin/Medicine"));
const AdminTypeAheadLazy = lazy(() => import("./pages/Admin/TypeAhead"));
import LoginForm from "./pages/auth/login/LoginForm";
import Consent from "./pages/auth/register/consent";
import OnboardingInfo from "./pages/Register/OnboardingInfo";
import RegistrationSuccess from "./pages/Register/RegistrationSuccess";
const AdminAnalyticsLazy = lazy(() => import("./pages/Admin/Analytics"));
const AdminSubscriptionsLazy = lazy(
  () => import("./pages/Admin/Subscriptions")
);
const AdminOrganizationsLazy = lazy(
  () => import("./pages/Admin/Organizations")
);
const AdminContractsLazy = lazy(() => import("./pages/Admin/Contracts"));
const CorporateLandingLazy = lazy(() => import("./pages/Corporate"));
const AdminMyOrgLazy = lazy(() => import("./pages/Admin/MyOrg"));
const AdminMyOrgEmployeesLazy = lazy(
  () => import("./pages/Admin/MyOrg/Employees")
);
const AdminOrgEmployeesLazy = lazy(
  () => import("./pages/Admin/Organizations/Employees")
);
const AdminLoginLazy = lazy(() => import("./pages/Admin/Login"));
const AdminActivateLazy = lazy(() => import("./pages/Admin/ActivateAdmin"));
const AdminUsersLazy = lazy(() => import("./pages/Admin/Users"));
const AdminCampaignsLazy = lazy(
  () => import("./pages/Admin/Marketing/Campaigns")
);
const AdminCreateEditCampaignLazy = lazy(
  () => import("./pages/Admin/Marketing/CreateEditCampaign")
);
const AdminCampaignDetailLazy = lazy(
  () => import("./pages/Admin/Marketing/Campaigns/CampaignDetail")
);
const AdminCampaignAnalyticsLazy = lazy(
  () => import("./pages/Admin/Marketing/Campaigns/CampaignAnalytics")
);
import HealthProfile from "@/pages/profile/health-profile/HealthProfile";
import ActivateEmergencyCardWrapper from "@/pages/profile/emergency-card/ActivateEmergencyCardWrapper";
import BloodGroup from "@/pages/profile/health-profile/BloodGroup";
import Notes from "@/pages/profile/health-profile/Notes";
import Vitals from "@/pages/profile/basic-information/Vitals";
import VitalsTracker from "@/pages/vitals-tracker/VitalsTracker";
import VitalsReadingsDetail from "@/pages/vitals-tracker/VitalsReadingsDetail";
import Surgeries from "./pages/profile/health-profile/surgeries/Surgeries";
import SurgeriesForm from "./pages/profile/health-profile/surgeries/SurgeriesForm";
import HealthMemoryList from "./pages/documents/health-memory/HealthMemoryList";
import HealthMemoryForm from "./pages/documents/health-memory/HealthMemoryForm";
import Conditions from "./pages/profile/health-profile/conditions/Conditions";
import ConditionsForm from "./pages/profile/health-profile/conditions/ConditionsForm";
import Medications from "./pages/profile/health-profile/medication/Medications";
import MedicationForm from "./pages/profile/health-profile/medication/MedicationForm";
import Allergies from "@/pages/profile/health-profile/allergies/Allergies";
import AddAllergy from "@/pages/profile/health-profile/allergies/AddAllergy";
import ImplantedDevices from "@/pages/profile/health-profile/implant-devices/ImplantedDevices";
import ImplantedDevicesForm from "@/pages/profile/health-profile/implant-devices/ImplantedDevicesForm";
import VaccinationTracker from "./pages/profile/health-profile/VaccineTracker";
import BirthComplications from "./pages/profile/health-profile/BirthComplications";
import Contact from "@/pages/profile/basic-information/Contact";
import PetCare from "./pages/profile/health-profile/PetCare";
import Identity from "./pages/profile/basic-information/Identity";
import { DebugMovementsPage } from "./pages/debug/DebugMovementsPage";
import InsuranceReminder from "./pages/reminders/insurance";
import BaseLayout from "./components/Layout/BaseLayout";
import AuthLayout from "./components/Layout/AuthLayout";
import ProfileLayout from "./components/Layout/ProfileLayout";

import AppointmentReminder from "./pages/reminders/appointment";
import { MToastProvider } from "@/components/ui/atoms/MToastProvider";
import DocumentsLanding from "./pages/documents/DocumentsLanding";
import DocumentsNavigation from "./pages/documents/DocumentsNavigation";
import PillboxLanding from "./pages/pillbox/PillboxLanding";
import AddMedicine from "./pages/pillbox/AddMedicine";
import MedicineDetail from "./pages/pillbox/MedicineDetail";
import SosLanding from "./pages/pillbox/SosLanding";
import ShoppingList from "./pages/pillbox/ShoppingList";
import PastMedications from "./pages/pillbox/PastMedications";
import ReviewMedications from "./pages/pillbox/ReviewMedications";
import ViewPrescriptions from "./pages/pillbox/ViewPrescriptions";
import ChoosePrescriptionVersion from "./pages/pillbox/ChoosePrescriptionVersion";
import UploadProgress from "./pages/documents/UploadProgress";
import AllUploads from "./pages/documents/AllUploads";
import MedicationReminder from "./pages/reminders/medication";
import VitalsReminder from "./pages/reminders/vitals";
import VaccinationFormPage from "./pages/profile/health-profile/VaccineTracker/VaccinationFormPage";
import SuggestedVaccinesPage from "./pages/profile/health-profile/VaccineTracker/SuggestedVaccinesPage";
import AddVaccinePage from "./pages/profile/health-profile/VaccineTracker/AddVaccinePage";
import PetCareFormPage from "./pages/profile/health-profile/PetCare/PetCareFormPage";
import PetCareHistory from "./pages/profile/health-profile/PetCare/PetCareHistory";
import EditReminderFormPage from "./pages/reminders/EditReminderFormPage";
import { Helmet } from "react-helmet";
import InviteRegistrationSuccess from "./pages/auth/register/InviteRegistrationSuccess";
import DocumentLogListing from "./pages/documents/DocumentLogListing";
import QuickAccess from "./pages/documents/QuickAccess";
import Subscriptions from "./pages/profile/subscription";
import { useAppAnalytics } from "./hooks/useAppAnalytics";
import { useGTMAnalytics } from "./hooks/useGTMAnalytics";
import { initGTM } from "./utils/gtm";
import { useConfirmFooterFocusVisibility } from "./hooks/useConfirmFooterFocusVisibility";
import { useFitStandaloneWindow } from "./hooks/useFitStandaloneWindow";
import { useInstallGate } from "./hooks/useInstallGate";
import { setupMobileVH } from "./utils/mobile-vh";
import { setupAndroidViewportReset } from "./utils/androidViewportReset";

function App() {
  // Route protection is now handled by AuthGuard component
  useAppAnalytics();
  useGTMAnalytics(); // Initialize GTM tracking
  useConfirmFooterFocusVisibility();
  useFitStandaloneWindow();
  const {
    isGating,
    canInstall,
    isIos,
    isMacSafari,
    handleInstall,
    handleCancel,
  } = useInstallGate();

  // Initialize GTM on app mount
  React.useEffect(() => {
    initGTM();
  }, []);

  // Initialize mobile scroll and keyboard fixes

  const APP_REDIRECT = false;

  React.useEffect(() => setupMobileVH(), []);

  React.useEffect(() => setupAndroidViewportReset(), []);

  React.useEffect(() => {
    if (!APP_REDIRECT || isGating) return;
    if (typeof window === "undefined") return;
    const isInReactNativeWebView = Boolean((window as any).ReactNativeWebView);
    setTimeout(() => {
      if (!isInReactNativeWebView) {
        window.location.assign("https://maatra.health/download");
      }
    }, 50);
  }, [isGating, APP_REDIRECT]);

  if (isGating) {
    return (
      <MInstallGateScreen
        canInstall={canInstall}
        isIos={isIos}
        isMacSafari={isMacSafari}
        onInstall={handleInstall}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <>
      <MInstallAppBanner />
      <Helmet>
        {/* Allow zoom for keyboard to work, prevent pinch via CSS */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no"
        />
      </Helmet>
      <div className="app-root">
        <Routes>
          {/* Profile Drawer Route */}
          <Route path="/profile" element={<ProfileDrawerRoute />}></Route>
          <Route path={PublicRoutePath.root} element={<RootRedirect />} />

          <Route path="/register/member" element={<RegisterMember />} />
          <Route
            path={PublicRoutePath.componentDemo}
            element={<ComponentDemo />}
          />
          <Route path={PublicRoutePath.buttonTest} element={<ButtonTest />} />
          <Route path={PublicRoutePath.mfilterTest} element={<MFilterTest />} />
          <Route path={PublicRoutePath.inputTest} element={<InputTest />} />
          <Route path={PublicRoutePath.avatarTest} element={<AvatarTest />} />
          <Route path={PublicRoutePath.chipDemo} element={<ChipDemo />} />
          <Route path={PublicRoutePath.toastTest} element={<ToastTest />} />
          <Route path={PublicRoutePath.minfoTest} element={<MInfoTest />} />
          <Route
            path={PublicRoutePath.mbuttonCardTest}
            element={<MButtonCardTest />}
          />
          <Route
            path={PublicRoutePath.textAreaTest}
            element={<TestTextArea />}
          />
          <Route
            path={PublicRoutePath.listItemDemo}
            element={<ListItemDemo />}
          />
          <Route
            path={PublicRoutePath.optionsMenuTest}
            element={<OptionsMenuTest />}
          />
          <Route
            path={PublicRoutePath.navigationBarDemo}
            element={<NavigationBarDemo />}
          />
          <Route
            path={PublicRoutePath.profilePickerDemo}
            element={<ProfilePickerDemo />}
          />
          <Route
            path={PublicRoutePath.integratedMobileDemo}
            element={<IntegratedMobileDemo />}
          />
          <Route
            path={PublicRoutePath.checkboxTest}
            element={<CheckboxTest />}
          />
          <Route path={PublicRoutePath.radioTest} element={<RadioTest />} />
          <Route
            path={PublicRoutePath.progressTest}
            element={<ProgressTest />}
          />
          <Route path="/uitest/progress-bar" element={<ProgressBarTest />} />

          <Route path={PublicRoutePath.toggleTest} element={<ToggleTest />} />
          <Route path={PublicRoutePath.dialogTest} element={<DialogTest />} />
          <Route
            path={PublicRoutePath.modalDialogTest}
            element={<ModalDialogTest />}
          />

          {/* Public Document Share Link */}
          <Route
            path="/share/document"
            element={
              <Suspense>
                <ShareDocument />
              </Suspense>
            }
          />

          <Route
            path="/signup"
            element={
              <AuthGuard requireAuth={false}>
                <SignupLanding />
              </AuthGuard>
            }
          />
          <Route path="/desktop" element={<DesktopInstallPage />} />
          <Route path="/how-maatra-works" element={<HowMaatraWorks />} />
          <Route path="/explore-maatra" element={<WhatCanMaatraDo />} />
          <Route
            path="/explore-maatra/ongoing-care"
            element={<OngoingCare />}
          />
          <Route
            path="/explore-maatra/remote-family"
            element={<RemoteFamily />}
          />
          <Route
            path="/explore-maatra/scattered-records"
            element={<ScatteredRecords />}
          />
          <Route
            path="/explore-maatra/hospitalisation"
            element={<Hospitalisation />}
          />

          {/* AUTH ROUTES WRAPPED IN AUTHLAYOUT */}
          <Route element={<AuthLayout />}>
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/register/info" element={<OnboardingInfo />} />
            <Route path="/register/consent" element={<Consent />} />
            <Route path="/register/success" element={<RegistrationSuccess />} />
            <Route path="/register/signupoptions" element={<SignUpOptions />} />
            <Route path="/google/callback" element={<AuthCallback />} />
            <Route
              path="/login"
              element={<Navigate to="/login/form" replace />}
            />
            <Route
              path="/login/form"
              element={
                <Suspense>
                  <LoginForm />
                </Suspense>
              }
            />
            <Route path="/register/SignupForm/:mode" element={<SignupForm />} />
            <Route path="/register/signupconfirm" element={<SignupConfirm />} />
            <Route
              path="/register/success"
              element={<RegistrationSuccessSplash />}
            />
            <Route
              path="/register/invite-success"
              element={<InviteRegistrationSuccess />}
            />
          </Route>

          {/* Grant Access Consent Page */}
          <Route path="/member/grant-access" element={<GrantAccessPage />} />

          {/* Dashboard Route (HomeLayout) - Protected */}
          <Route element={<HomeLayout />}>
            <Route
              path="/"
              element={
                <AuthGuard requireAuth={true}>
                  <Dashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AuthGuard requireAuth={true}>
                  <Dashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/dashboard-detail"
              element={
                <AuthGuard requireAuth={true}>
                  <DashboardDetail />
                </AuthGuard>
              }
            />
          </Route>

          <Route element={<ProfileLayout hideFooterNav />}>
            <Route
              path="/profile/basic-information"
              element={<BasicInformation />}
            />
            <Route path="/profile/health-profile" element={<HealthProfile />} />
          </Route>
          <Route
            path="/profile/emergency-card/activate"
            element={<ActivateEmergencyCardWrapper />}
          />

          <Route element={<Layout hideBack showGradient hideProfilePicker />}>
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/dashboard/updates" element={<Updates />} />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker showGradient />}>
            <Route path="/vitals-tracker" element={<VitalsTracker />} />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker />}>
            <Route
              path="/vitals-tracker/readings/:parameter"
              element={<VitalsReadingsDetail />}
            />
          </Route>
          {/* Lab insights pages run footer-less by product spec — they own
              their own bottom navigation (the constellation back-out, tabs,
              etc.) and the global footer would crowd the lavender section. */}
          <Route element={<Layout hideBack showGradient hideProfilePicker />}>
            <Route path="/lab-insights" element={<LabInsights />} />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker />}>
            <Route
              path="/lab-insights/all-groups"
              element={<LabInsightsAllGroups />}
            />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker />}>
            <Route
              path="/lab-insights/groups/:groupId"
              element={<LabInsightsGroupDetails />}
            />
          </Route>
          {/* Bare parent paths used by URL-segment back navigation. They
              redirect to the closest meaningful page in the lab-insights flow. */}
          <Route
            path="/lab-insights/groups"
            element={<Navigate to="/lab-insights/all-groups" replace />}
          />
          <Route
            path="/lab-insights/parameters"
            element={<Navigate to="/lab-insights" replace />}
          />
          <Route element={<Layout hideBack hideProfilePicker hideFooterNav />}>
            <Route
              path="/lab-insights/parameters/:canonicalParameterId"
              element={<LabInsightsParameterDetails />}
            />
          </Route>
          <Route
            element={
              <Layout showGradient hideFooterNav hideProfilePicker hideBack />
            }
          >
            <Route path="/health-summary" element={<HealthSummaryLanding />} />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path="/health-summary/details"
              element={
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center min-h-screen">
                      Loading...
                    </div>
                  }
                >
                  <HealthSummaryDetails />
                </Suspense>
              }
            />
          </Route>

          <Route element={<Layout hideBack showGradient hideProfilePicker />}>
            <Route path="/profile/family" element={<FamilyLanding />} />
          </Route>

          <Route element={<SuccessPageLayout />}>
            <Route
              path="/profile/family/add-member/success"
              element={<AddMemberSuccess />}
            />
            <Route
              path="/profile/family/invite-success"
              element={<InviteSuccess />}
            />
            <Route
              path="/profile/family/delete-member/confirm"
              element={<DeleteConfirm />}
            />
          </Route>

          <Route element={<NoFooterLayout hideExit />}>
            <Route
              path="/profile/subscription/manage"
              element={<ManageSubscription />}
            />
            <Route path="/new-subscription" element={<NewSubscription />} />
          </Route>

          <Route element={<NoFooterLayout showGradient hideExit />}>
            <Route
              path="/profile/subscription/purchase-history"
              element={<PurchaseHistory />}
            />
          </Route>

          <Route element={<SuccessPageLayout />}>
            <Route
              path="/subscription-success"
              element={<SubscriptionSuccess />}
            />
          </Route>

          {/* Full-page Document Viewer route, no header/footer */}
          <Route
            element={<NoFooterLayout exitPath="/documents/list" hideExit />}
          >
            <Route path="/documents/view" element={<DocumentViewer />} />
            <Route path="/documents/list/view" element={<DocumentViewer />} />
            <Route
              path="/health-summary/details/view"
              element={
                <React.Suspense fallback={null}>
                  <HealthSummaryViewer />
                </React.Suspense>
              }
            />
            <Route
              path="/documents/health-summary/view"
              element={
                <React.Suspense fallback={null}>
                  <HealthSummaryViewer />
                </React.Suspense>
              }
            />
            <Route path="/documents/all-uploads" element={<AllUploads />} />
            <Route
              path="/documents/uncategorized/view"
              element={<DocumentViewer />}
            />
            <Route
              path="/documents/uncategorized"
              element={<UncategorizedDocuments />}
            />
            <Route path="/documents/notes" element={<NotesListing />} />
            <Route
              path="/documents/health-memory/add"
              element={<HealthMemoryForm />}
            />
            <Route
              path="/documents/health-memory/edit"
              element={<HealthMemoryForm />}
            />
          </Route>

          <Route
            element={
              <NoFooterLayout exitPath="/documents/list" hideExit hideBack />
            }
          >
            <Route
              path="/dashboard/emergency-card"
              element={<EmergencyCardViewer />}
            />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path="/documents/list/select"
              element={<DocumentSelection />}
            />
          </Route>
          <Route
            element={
              <NoFooterLayout
                hideExit
                showGradient={true}
                hideBack
                showFooter
              />
            }
          >
            <Route path="/documents/quick-access" element={<QuickAccess />} />
          </Route>

          <Route element={<NoFooterLayout hideExit />}>
            <Route path="/profile/notifications" element={<Notifications />} />
            <Route
              path="/profile/notifications/oss-licenses"
              element={<OSSLicenses />}
            />
            <Route
              path="/profile/privacy-and-security"
              element={<PrivacyAndSecurity />}
            />
            <Route
              path="/profile/notifications/consent-withdrawal"
              element={<ConsentWithdrawal />}
            />
            <Route path="/profile/support" element={<Support />} />
          </Route>

          <Route element={<NoFooterLayout showGradient hideExit />}>
            <Route path="/profile/subscription" element={<Subscriptions />} />
          </Route>

          <Route
            element={<LandingPageLayout showGradient hideBack showFooter />}
          >
            <Route
              path="/profile/basic-information/insurance"
              element={<Insurance />}
            />
          </Route>
          <Route element={<LandingPageLayout showGradient showFooter />}>
            <Route
              path="/profile/health-profile/vaccination-tracker"
              element={<VaccinationTracker />}
            />
            <Route
              path="/reminders/vaccination-tracker"
              element={<VaccinationTracker />}
            />
            <Route
              path="/profile/health-profile/pet-care"
              element={<PetCare />}
            />
            <Route path="/reminders/pet-care" element={<PetCare />} />
          </Route>

          <Route
            element={<LandingPageLayoutWithBack showGradient showFooter />}
          >
            <Route
              path="/profile/health-profile/allergies"
              element={<Allergies />}
            />
            <Route
              path="/profile/health-profile/implant-devices"
              element={<ImplantedDevices />}
            />
            <Route
              path="/profile/health-profile/surgeries"
              element={<Surgeries />}
            />
            <Route
              path="/profile/health-profile/conditions"
              element={<Conditions />}
            />
            <Route
              path="/profile/health-profile/medication"
              element={<Medications />}
            />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker hideFooterNav />}>
            <Route
              path="/documents/upload-review"
              element={<UploadReviewPage />}
            />
          </Route>

          <Route element={<NoFooterLayout hideExit />}>
            <Route
              path="/profile/basic-information/identity"
              element={<Identity />}
            />
            <Route
              path="/profile/basic-information/contact"
              element={<Contact />}
            />
            <Route
              path="/profile/basic-information/vitals"
              element={<Vitals />}
            />
            <Route
              path="/profile/health-profile/blood-group"
              element={<BloodGroup />}
            />
            <Route
              path="/profile/health-profile/allergies/add-allergy"
              element={<AddAllergy />}
            />
            <Route
              path="/profile/health-profile/add-allergy"
              element={<AddAllergy />}
            />
            <Route
              path="/profile/health-profile/conditions/form"
              element={<ConditionsForm />}
            />
            <Route
              path="/profile/health-profile/medication/medicationform"
              element={<MedicationForm />}
            />
            <Route
              path="/profile/health-profile/surgeries/SurgeriesForm"
              element={<SurgeriesForm />}
            />
            <Route
              path="/profile/health-profile/implant-devices/form"
              element={<ImplantedDevicesForm />}
            />
            <Route path="/profile/health-profile/notes" element={<Notes />} />
            <Route
              path="/profile/health-profile/pet-care/add-update-schedule"
              element={<PetCareFormPage />}
            />
            <Route
              path="/profile/health-profile/pet-care/history"
              element={<PetCareHistory />}
            />
            <Route
              path="/reminders/pet-care/add-update-schedule"
              element={<PetCareFormPage />}
            />
            <Route
              path="/reminders/pet-care/history"
              element={<PetCareHistory />}
            />
            <Route
              path="/profile/health-profile/birth-complications"
              element={<BirthComplications />}
            />
            <Route
              path="/profile/basic-information/insurance/add"
              element={<AddInsurance />}
            />
          </Route>

          <Route
            element={
              <NoFooterLayout exitPath="/profile/basic-information/insurance" />
            }
          >
            <Route
              path="/profile/basic-information/insurance/type"
              element={<InsuranceType />}
            />
            <Route
              path="/profile/basic-information/insurance/type/form"
              element={<InsuranceForm />}
            />
            <Route
              path="/profile/basic-information/insurance/form"
              element={<InsuranceForm />}
            />
            <Route
              path="/profile/basic-information/insurance/link-family"
              element={<LinkFamilyPolicy />}
            />
            <Route
              path="/profile/basic-information/insurance/type/link-family"
              element={<LinkFamilyPolicy />}
            />
          </Route>

          <Route element={<NoFooterLayout />}>
            <Route path="/profile/health-profile" element={<HealthProfile />} />
            <Route
              path="/profile/health-profile/vaccination-tracker/add-vaccine-options"
              element={<AddVaccinePage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/suggested-vaccines"
              element={<SuggestedVaccinesPage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/suggested-vaccines/add-vaccine"
              element={<VaccinationFormPage />}
            />

            <Route
              path="/reminders/vaccination-tracker/add-vaccine-options"
              element={<AddVaccinePage />}
            />
            <Route
              path="/reminders/vaccination-tracker/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/reminders/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/reminders/vaccination-tracker/suggested-vaccines"
              element={<SuggestedVaccinesPage />}
            />
            <Route
              path="/reminders/vaccination-tracker/suggested-vaccines/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/profile/family/add-member/form"
              element={<AddMemberName />}
            />
            <Route
              path="/profile/family/add-member/pet-details"
              element={<AddPetDetails />}
            />
            <Route
              path="/profile/family/add-member/form/details"
              element={<AddMemberDetails />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite"
              element={<AddMemberInvite />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/email"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/mobile"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/consent"
              element={<AddMemberConsent />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/consent"
              element={<AddMemberConsent />}
            />
            <Route
              path="/profile/family/add-member/form/details/consent"
              element={<AddMemberConsent />}
            />
          </Route>

          <Route
            path="/profile/family/member/:memberId"
            element={<MemberDetailWrapper />}
          />
          <Route element={<NoFooterLayout hideExit showGradient />}>
            <Route
              path="/profile/family/edit/:memberId"
              element={<UpdateProfile />}
            />
          </Route>
          {/* All overview screens render their own purple-gradient header
              (with its own Back button) instead of the standard MHeader —
              hideBack here stops NoFooterLayout's MTopBar from rendering a
              second one. */}
          <Route element={<NoFooterLayout hideExit showGradient hideBack />}>
            <Route
              path="/profile/family/documents-overview"
              element={<DocumentsOverview />}
            />
            <Route
              path="/profile/family/health-memories-overview"
              element={<HealthMemoriesOverview />}
            />
            <Route
              path="/profile/family/reminders-overview"
              element={<RemindersOverview />}
            />
            <Route
              path="/profile/family/emergency-cards-overview"
              element={<EmergencyCardsOverview />}
            />
            <Route
              path="/profile/family/health-profiles-overview"
              element={<HealthProfilesOverview />}
            />
            <Route
              path="/profile/family/health-summary-overview"
              element={<HealthSummaryOverview />}
            />
            <Route
              path="/profile/family/pillbox-overview"
              element={<PillboxOverview />}
            />
            <Route
              path="/profile/family/vitals-overview"
              element={<VitalsOverview />}
            />
            <Route
              path="/profile/family/lab-trends-overview"
              element={<LabTrendsOverview />}
            />
          </Route>

          <Route element={<NoFooterLayout hideExit />}>
            <Route
              path="/profile/family/add-member/pet-details/profile"
              element={<AddPetProfile />}
            />
            <Route
              path="/profile/family/invite-member"
              element={<InviteMember />}
            />
            <Route
              path="/profile/family/invite-contact-method"
              element={<InviteContactMethod />}
            />
            <Route
              path="/profile/family/delete-member"
              element={<DeleteProfile />}
            />
            <Route
              path="/profile/family/manage-access/access-request"
              element={
                <React.Suspense fallback={null}>
                  <ManageMyAccess />
                </React.Suspense>
              }
            />
            <Route
              path="/profile/family/manage-access/member-can-access"
              element={
                <React.Suspense fallback={null}>
                  <ManageMyAccess />
                </React.Suspense>
              }
            />
            <Route
              path="/profile/family/notes/detail"
              element={<FamilyNoteDetail />}
            />
          </Route>

          <Route element={<NoFooterLayout showGradient hideExit />}>
            <Route path="/profile/family/notes" element={<FamilyNotes />} />
          </Route>
          <Route element={<NoFooterLayout hideExit />}>
            <Route
              path="/profile/family/manage-access"
              element={<ManageAccess />}
            />
            <Route
              path="/profile/family/add-member"
              element={<SelectRelationship />}
            />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path="/reminders/appointment"
              element={<AppointmentReminder />}
            />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path="/reminders/insurance"
              element={<InsuranceReminder />}
            />
            <Route
              path="/reminders/medication"
              element={<MedicationReminder />}
            />
            <Route path="/reminders/vitals" element={<VitalsReminder />} />
          </Route>

          <Route element={<Layout />}>
            <Route path="/profile" element={<ProfileDrawerRoute />} />
          </Route>

          {/* Debug Routes — authenticated, no layout */}
          <Route
            path="/debug/movements"
            element={
              <AuthGuard requireAuth={true}>
                <DebugMovementsPage />
              </AuthGuard>
            }
          />

          {/* Admin Login — no guard, no layout */}
          <Route
            path="/admin/login"
            element={
              <Suspense>
                <AdminLoginLazy />
              </Suspense>
            }
          />
          {/* Admin Activate — public invite activation page */}
          <Route
            path="/admin/activate"
            element={
              <Suspense>
                <AdminActivateLazy />
              </Suspense>
            }
          />
          {/* Admin Routes — guarded */}
          <Route
            path="/admin"
            element={
              <AdminAuthGuard>
                <Suspense>
                  <AdminIndexLazy />
                </Suspense>
              </AdminAuthGuard>
            }
          />
          <Route
            element={
              <AdminAuthGuard>
                <AdminLayout />
              </AdminAuthGuard>
            }
          >
            <Route
              path="/admin/dashboard"
              element={
                <Suspense>
                  <AdminDashboardLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <Suspense>
                  <AdminRolesLazy />
                </Suspense>
              }
            />
            <Route path="/admin/audit-logs" element={<div>Audit Logs</div>} />
            <Route
              path="/admin/subscriptions"
              element={
                <Suspense>
                  <AdminSubscriptionsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/prompts"
              element={
                <Suspense>
                  <AdminPromptsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/medications"
              element={
                <Suspense>
                  <AdminMedicineLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/typeahead"
              element={
                <Suspense>
                  <AdminTypeAheadLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/settings"
              element={<div>System Settings</div>}
            />
            <Route
              path="/admin/analytics"
              element={
                <Suspense>
                  <AdminAnalyticsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/organizations"
              element={
                <Suspense>
                  <AdminOrganizationsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/contracts"
              element={
                <Suspense>
                  <AdminContractsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/users"
              element={
                <Suspense>
                  <AdminUsersLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/my-org"
              element={
                <Suspense>
                  <AdminMyOrgLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/my-org/employees"
              element={
                <Suspense>
                  <AdminMyOrgEmployeesLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/organizations/:orgId/employees"
              element={
                <Suspense>
                  <AdminOrgEmployeesLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns"
              element={
                <Suspense>
                  <AdminCampaignsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/create"
              element={
                <Suspense>
                  <AdminCreateEditCampaignLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/analytics"
              element={
                <Suspense>
                  <AdminCampaignAnalyticsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/:id/edit"
              element={
                <Suspense>
                  <AdminCreateEditCampaignLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/:id"
              element={
                <Suspense>
                  <AdminCampaignDetailLazy />
                </Suspense>
              }
            />
          </Route>
          {/* Signup Input Route */}
          <Route path="/register/SignupForm/:mode" element={<SignupForm />} />
          {/* Signup Confirm Route */}
          <Route path="/register/signupconfirm" element={<SignupConfirm />} />
          {/* Registration Success Splash Route */}
          <Route
            path="/register/success"
            element={<RegistrationSuccessSplash />}
          />
          {/* Signup Options Route */}
          <Route
            path="/signupoptions"
            element={
              <Suspense>
                <SignUpOptions />
              </Suspense>
            }
          />
          {/* Options Menu Test Route */}
          <Route path="/uitest/options-menu" element={<OptionsMenuTest />} />
          {/* Date Picker Test Route */}
          <Route
            path={PublicRoutePath.drawerModulesTest}
            element={<DrawerModulesTest />}
          />
          {/* Infinite Scroll Test Route */}
          <Route
            path={PublicRoutePath.minfiniteScrollTest}
            element={<MInfiniteScrollTest />}
          />
          <Route>
            <Route path="/register/SignupForm/:mode" element={<SignupForm />} />
            <Route path="/register/signupconfirm" element={<SignupConfirm />} />
            <Route
              path="/register/success"
              element={<RegistrationSuccessSplash />}
            />
            <Route
              path="/register/invite-success"
              element={<InviteRegistrationSuccess />}
            />
          </Route>

          {/* Grant Access Consent Page */}
          <Route path="/member/grant-access" element={<GrantAccessPage />} />

          {/* Dashboard Route (HomeLayout) - Protected */}
          <Route element={<HomeLayout />}>
            <Route
              path="/"
              element={
                <AuthGuard requireAuth={true}>
                  <Dashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AuthGuard requireAuth={true}>
                  <Dashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/dashboard-detail"
              element={
                <AuthGuard requireAuth={true}>
                  <DashboardDetail />
                </AuthGuard>
              }
            />
          </Route>

          <Route element={<ProfileLayout />}>
            <Route
              path="/profile/basic-information"
              element={<BasicInformation />}
            />
            <Route path="/profile/health-profile" element={<HealthProfile />} />
          </Route>
          <Route element={<Layout hideBack showGradient hideProfilePicker />}>
            <Route
              path="/documents"
              element={
                <MToastProvider>
                  <DocumentsLanding />
                </MToastProvider>
              }
            />
          </Route>

          <Route element={<Layout hideBack showGradient hideProfilePicker />}>
            <Route path="/pillbox" element={<PillboxLanding />} />
            <Route path="/pillbox/sos" element={<SosLanding />} />
            <Route path="/pillbox/shopping-list" element={<ShoppingList />} />
            <Route
              path="/pillbox/past-medications"
              element={<PastMedications />}
            />
          </Route>

          <Route
            element={
              <Layout
                hideBack
                hideProfilePicker
                hideFooterNav
                hideTopBar
                showGradient
              />
            }
          >
            <Route path="/pillbox/add-medicine" element={<AddMedicine />} />
            <Route
              path="/pillbox/review/choose"
              element={<ChoosePrescriptionVersion />}
            />
          </Route>

          <Route
            element={
              <Layout hideBack hideProfilePicker hideFooterNav hideTopBar />
            }
          >
            <Route path="/pillbox/review" element={<ReviewMedications />} />
            <Route
              path="/pillbox/review/prescriptions"
              element={<ViewPrescriptions />}
            />
          </Route>

          <Route element={<Layout hideFooterNav noTopBar />}>
            <Route
              path="/pillbox/:memberId/:itemType/:id"
              element={<MedicineDetail />}
            />
          </Route>

          <Route
            element={
              <Layout
                hideProfilePicker
                hideFooterNav
                hideBack
                skipExitConfirmation
                exitPath="/documents"
              />
            }
          >
            <Route
              path="/documents/upload-progress"
              element={
                <MToastProvider>
                  <UploadProgress />
                </MToastProvider>
              }
            />
          </Route>

          <Route element={<Layout hideBack showGradient />}>
            <Route path="/reminders" element={<Reminders />} />
            <Route path="/insights" element={<Insights />} />
          </Route>
          {/* Lab insights pages run footer-less by product spec. */}
          <Route element={<Layout hideBack showGradient hideFooterNav />}>
            <Route path="/lab-insights" element={<LabInsights />} />
          </Route>
          <Route element={<Layout hideBack hideProfilePicker />}>
            <Route
              path="/lab-insights/:groupId"
              element={<LabInsightsGroupDetails />}
            />
          </Route>
          <Route element={<Layout hideBack hideFooterNav />}>
            <Route
              path="/lab-insights/all-groups"
              element={<LabInsightsAllGroups />}
            />
            <Route
              path="/lab-insights/groups/:groupId"
              element={<LabInsightsGroupDetails />}
            />
          </Route>
          {/* Bare parent paths used by URL-segment back navigation. */}
          <Route
            path="/lab-insights/groups"
            element={<Navigate to="/lab-insights/all-groups" replace />}
          />
          <Route
            path="/lab-insights/parameters"
            element={<Navigate to="/lab-insights" replace />}
          />
          <Route element={<Layout hideBack hideFooterNav />}>
            <Route
              path="/lab-insights/parameters/:canonicalParameterId"
              element={<LabInsightsParameterDetails />}
            />
          </Route>

          <Route element={<Layout hideBack showGradient />}>
            <Route path="/profile/family" element={<FamilyListing />} />
          </Route>

          <Route element={<SuccessPageLayout />}>
            <Route
              path="/profile/family/add-member/success"
              element={<AddMemberSuccess />}
            />
            <Route
              path="/profile/family/invite-success"
              element={<InviteSuccess />}
            />
            <Route
              path="/profile/family/delete-member/confirm"
              element={<DeleteConfirm />}
            />
          </Route>
          <Route
            element={
              <NoFooterLayout
                exitPath="/documents/list"
                hideExit
                showGradient
                hideBack
                showFooter
              />
            }
          >
            <Route path="/documents/list" element={<DocumentListing />} />
          </Route>

          <Route
            element={
              <NoFooterLayout
                exitPath="/documents/list"
                hideExit
                showGradient
                hideBack
                showFooter
              />
            }
          >
            <Route
              path="/documents/navigation"
              element={<DocumentsNavigation />}
            />
          </Route>

          <Route
            element={
              <NoFooterLayout
                exitPath="/documents/list"
                hideExit
                hideBack
                showGradient
              />
            }
          >
            <Route
              path="/documents/log-listing"
              element={<DocumentLogListing />}
            />
          </Route>

          {/* Full-page Document Viewer route, no header/footer */}
          <Route
            element={<NoFooterLayout exitPath="/documents/list" hideExit />}
          >
            <Route path="/documents/view" element={<DocumentViewer />} />
            <Route path="/documents/list/view" element={<DocumentViewer />} />
            <Route
              path="/documents/quick-access/view"
              element={<DocumentViewer />}
            />
            <Route
              path="/documents/uncategorized/view"
              element={<DocumentViewer />}
            />
            <Route
              path="/documents/uncategorized"
              element={<UncategorizedDocuments />}
            />
            <Route path="/documents/notes" element={<NotesListing />} />
            <Route
              path="/documents/health-memory/add"
              element={<HealthMemoryForm />}
            />
            <Route
              path="/documents/health-memory/edit"
              element={<HealthMemoryForm />}
            />
          </Route>

          <Route element={<NoFooterLayout exitPath="/profile" />}>
            <Route path="/profile/notifications" element={<Notifications />} />
            <Route path="/profile/support" element={<Support />} />
          </Route>

          <Route element={<LandingPageLayout showGradient />}>
            <Route
              path="/profile/basic-information/insurance"
              element={<Insurance />}
            />
            <Route
              path="/profile/health-profile/blood-group"
              element={<BloodGroup />}
            />
          </Route>

          <Route element={<LandingPageLayoutWithBack showGradient />}>
            <Route
              path="/profile/health-profile/allergies"
              element={<Allergies />}
            />
            <Route
              path="/profile/health-profile/implant-devices"
              element={<ImplantedDevices />}
            />
            <Route
              path="/profile/health-profile/surgeries"
              element={<Surgeries />}
            />
            <Route
              path="/profile/health-profile/conditions"
              element={<Conditions />}
            />
            <Route
              path="/profile/health-profile/medication"
              element={<Medications />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker"
              element={<VaccinationTracker />}
            />
            <Route
              path="/reminders/vaccination-tracker"
              element={<VaccinationTracker />}
            />
            <Route
              path="/profile/health-profile/birth-complications"
              element={<BirthComplications />}
            />
            <Route
              path="/profile/health-profile/pet-care"
              element={<PetCare />}
            />
            <Route path="/reminders/pet-care" element={<PetCare />} />
          </Route>

          <Route element={<NoFooterLayout />}>
            <Route
              path="/profile/basic-information/vitals"
              element={<Vitals />}
            />
            <Route
              path="/profile/basic-information/identity"
              element={<Identity />}
            />
            <Route
              path="/profile/basic-information/contact"
              element={<Contact />}
            />
            <Route
              path="/profile/basic-information/insurance/type"
              element={<InsuranceType />}
            />
            <Route
              path="/profile/basic-information/insurance/type/form"
              element={<InsuranceForm />}
            />
            <Route
              path="/profile/basic-information/insurance/form"
              element={<InsuranceForm />}
            />
            <Route
              path="/profile/basic-information/insurance/link-family"
              element={<LinkFamilyPolicy />}
            />
            <Route
              path="/profile/basic-information/insurance/type/link-family"
              element={<LinkFamilyPolicy />}
            />
            <Route path="/profile/health-profile" element={<HealthProfile />} />

            <Route
              path="/profile/health-profile/implant-devices/form"
              element={<ImplantedDevicesForm />}
            />

            <Route
              path="/profile/health-profile/surgeries/SurgeriesForm"
              element={<SurgeriesForm />}
            />

            <Route
              path="/profile/health-profile/conditions/form"
              element={<ConditionsForm />}
            />
            <Route
              path="/profile/health-profile/medication/medicationform"
              element={<MedicationForm />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/add-vaccine-options"
              element={<AddVaccinePage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/suggested-vaccines"
              element={<SuggestedVaccinesPage />}
            />
            <Route
              path="/profile/health-profile/vaccination-tracker/suggested-vaccines/add-vaccine"
              element={<VaccinationFormPage />}
            />

            <Route
              path="/reminders/vaccination-tracker/add-vaccine-options"
              element={<AddVaccinePage />}
            />
            <Route
              path="/reminders/vaccination-tracker/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/reminders/add-vaccine"
              element={<VaccinationFormPage />}
            />
            <Route
              path="/reminders/vaccination-tracker/suggested-vaccines"
              element={<SuggestedVaccinesPage />}
            />
            <Route
              path="/reminders/vaccination-tracker/suggested-vaccines/add-vaccine"
              element={<VaccinationFormPage />}
            />

            <Route path="/profile/health-profile/notes" element={<Notes />} />

            <Route
              path="/profile/health-profile/pet-care/add-update-schedule"
              element={<PetCareFormPage />}
            />
            <Route
              path="/profile/health-profile/pet-care/history"
              element={<PetCareHistory />}
            />

            <Route
              path="/reminders/pet-care/add-update-schedule"
              element={<PetCareFormPage />}
            />
            <Route
              path="/reminders/pet-care/history"
              element={<PetCareHistory />}
            />

            <Route
              path="/profile/family/add-member/form"
              element={<AddMemberName />}
            />
            <Route
              path="/profile/family/add-member/pet-details"
              element={<AddPetDetails />}
            />
            <Route
              path="/profile/family/add-member/form/details"
              element={<AddMemberDetails />}
            />
            <Route
              path="/profile/family/add-member/form/details/spouse"
              element={<SpouseAccess />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite"
              element={<AddMemberInvite />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/email"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/mobile"
              element={<AddMemberContact />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/contact/consent"
              element={<AddMemberConsent />}
            />
            <Route
              path="/profile/family/add-member/form/details/invite/consent"
              element={<AddMemberConsent />}
            />
            <Route
              path="/profile/family/add-member/form/details/consent"
              element={<AddMemberConsent />}
            />
            <Route
              path="/profile/family/member/:memberId"
              element={<MemberDetail />}
            />
            <Route
              path="/profile/family/edit/:memberId"
              element={<UpdateProfile />}
            />
            <Route
              path="/profile/family/add-member/pet-details/profile"
              element={<AddPetProfile />}
            />
            <Route
              path="/profile/family/invite-member"
              element={<InviteMember />}
            />
            <Route
              path="/profile/family/invite-contact-method"
              element={<InviteContactMethod />}
            />
            <Route
              path="/profile/family/delete-member"
              element={<DeleteProfile />}
            />
            <Route
              path="/profile/family/manage-access/access-request"
              element={
                <React.Suspense fallback={null}>
                  <ManageMyAccess />
                </React.Suspense>
              }
            />
            <Route
              path="/profile/family/notes/detail"
              element={<FamilyNoteDetail />}
            />
          </Route>
          <Route
            element={
              <NoFooterLayout hideExit hideBack showFooter showGradient />
            }
          >
            <Route
              path="/documents/health-memory"
              element={<HealthMemoryList />}
            />
          </Route>

          <Route element={<NoFooterLayout level={1} />}>
            <Route path="/reminders/edit" element={<EditReminderFormPage />} />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path="/reminders/insurance"
              element={<InsuranceReminder />}
            />
            <Route
              path="/reminders/appointment"
              element={<AppointmentReminder />}
            />
            <Route
              path="/reminders/medication"
              element={<MedicationReminder />}
            />
            <Route path="/reminders/vitals" element={<VitalsReminder />} />
          </Route>

          <Route element={<Layout />}>
            <Route path="/profile" element={<ProfileDrawerRoute />} />
          </Route>

          {/* Admin Login — no guard, no layout */}
          <Route
            path="/admin/login"
            element={
              <Suspense>
                <AdminLoginLazy />
              </Suspense>
            }
          />
          {/* Admin Activate — public invite activation page */}
          <Route
            path="/admin/activate"
            element={
              <Suspense>
                <AdminActivateLazy />
              </Suspense>
            }
          />
          {/* Admin Routes — guarded */}
          <Route
            path="/admin"
            element={
              <AdminAuthGuard>
                <Suspense>
                  <AdminIndexLazy />
                </Suspense>
              </AdminAuthGuard>
            }
          />
          <Route
            element={
              <AdminAuthGuard>
                <AdminLayout />
              </AdminAuthGuard>
            }
          >
            <Route
              path="/admin/dashboard"
              element={
                <Suspense>
                  <AdminDashboardLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <Suspense>
                  <AdminRolesLazy />
                </Suspense>
              }
            />
            <Route path="/admin/audit-logs" element={<div>Audit Logs</div>} />
            <Route
              path="/admin/subscriptions"
              element={
                <Suspense>
                  <AdminSubscriptionsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/prompts"
              element={
                <Suspense>
                  <AdminPromptsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/medications"
              element={
                <Suspense>
                  <AdminMedicineLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/typeahead"
              element={
                <Suspense>
                  <AdminTypeAheadLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/settings"
              element={<div>System Settings</div>}
            />
            <Route
              path="/admin/analytics"
              element={
                <Suspense>
                  <AdminAnalyticsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/organizations"
              element={
                <Suspense>
                  <AdminOrganizationsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/contracts"
              element={
                <Suspense>
                  <AdminContractsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/users"
              element={
                <Suspense>
                  <AdminUsersLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/my-org"
              element={
                <Suspense>
                  <AdminMyOrgLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/my-org/employees"
              element={
                <Suspense>
                  <AdminMyOrgEmployeesLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/organizations/:orgId/employees"
              element={
                <Suspense>
                  <AdminOrgEmployeesLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns"
              element={
                <Suspense>
                  <AdminCampaignsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/create"
              element={
                <Suspense>
                  <AdminCreateEditCampaignLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/analytics"
              element={
                <Suspense>
                  <AdminCampaignAnalyticsLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/:id/edit"
              element={
                <Suspense>
                  <AdminCreateEditCampaignLazy />
                </Suspense>
              }
            />
            <Route
              path="/admin/marketing/campaigns/:id"
              element={
                <Suspense>
                  <AdminCampaignDetailLazy />
                </Suspense>
              }
            />
          </Route>
          {/* Signup Input Route */}
          <Route path="/register/SignupForm/:mode" element={<SignupForm />} />
          {/* Signup Confirm Route */}
          <Route path="/register/signupconfirm" element={<SignupConfirm />} />
          {/* Registration Success Splash Route */}
          <Route
            path="/register/success"
            element={<RegistrationSuccessSplash />}
          />
          {/* Signup Options Route */}
          <Route
            path="/signupoptions"
            element={
              <Suspense>
                <SignUpOptions />
              </Suspense>
            }
          />
          {/* Options Menu Test Route */}
          <Route path="/uitest/options-menu" element={<OptionsMenuTest />} />
          {/* Date Picker Test Route */}
          <Route
            path={PublicRoutePath.drawerModulesTest}
            element={<DrawerModulesTest />}
          />
          {/* Infinite Scroll Test Route */}
          <Route
            path={PublicRoutePath.minfiniteScrollTest}
            element={<MInfiniteScrollTest />}
          />
          <Route path={PublicRoutePath.mframeTest} element={<MFrameTest />} />
          {/* MUST stay last — wildcard that matches org slugs e.g. /sapient */}
          <Route path="/:slug" element={<CorporateLandingLazy />} />
        </Routes>
        <Toaster />
      </div>
    </>
  );
}

// Drawer route wrapper for /profile and nested routes
function ProfileDrawerRoute() {
  const navigate = useNavigate();

  return (
    <ProfileLayout>
      <BaseNavigation
        open={true}
        onOpenChange={(open) => {
          if (!open) navigate("/dashboard");
        }}
        onNavigate={(route) =>
          navigate(route, { state: { backUrl: "/profile" } })
        }
        version="2.0"
      />
      <Outlet />
    </ProfileLayout>
  );
}

import { NotesProvider } from "@/context/NotesContext";
import { ManagedMembersProvider } from "@/context/ManagedMembersContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";

export default function AppWithProviders(props) {
  return (
    <AvatarProvider>
      <MToastProvider>
        <NotesProvider>
          <ManagedMembersProvider>
            <SubscriptionProvider>
              <App {...props} />
            </SubscriptionProvider>
          </ManagedMembersProvider>
        </NotesProvider>
      </MToastProvider>
    </AvatarProvider>
  );
}
